// @catalog F.030
// MarginDistribution — scatter chart distintivo de /margenes.
// X: unidades vendidas (log scale), Y: margen real %, R: margen total $.
// Cuadrantes: superior-derecho = Estrellas; inferior-derecho = Volumen sin margen.
import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

export interface ScatterProduct {
  id: string | number;
  name: string;
  unitsSold: number;
  realMarginPct: number;
  totalMargin: number;
  marginPct?: number;
}

interface MarginDistributionProps {
  products: ReadonlyArray<ScatterProduct>;
  height?: number;
  formatValue: (v: number) => string;
}

const PAD_L = 44;
const PAD_R = 16;
const PAD_T = 16;
const PAD_B = 36;

function bubbleColor(realMarginPct: number): string {
  if (realMarginPct < 15) return "var(--neg)";
  if (realMarginPct < 22) return "var(--warn)";
  return "var(--accent)";
}

export function MarginDistribution(props: MarginDistributionProps): JSX.Element {
  const { products: rawProducts, height = 320, formatValue } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(700);
  const [hover, setHover] = useState<number | null>(null);

  // Filtrar productos con valores NaN/inválidos antes de renderizar (defensive
  // guard — los hooks pueden devolver entries con marginPct undefined cuando
  // el backend no tiene costo registrado para ese producto).
  const products = rawProducts.filter(
    (p) =>
      Number.isFinite(p.unitsSold) &&
      p.unitsSold > 0 &&
      Number.isFinite(p.realMarginPct) &&
      Number.isFinite(p.totalMargin) &&
      p.totalMargin > 0,
  );

  useEffect(() => {
    if (!ref.current) return;
    const target = ref.current;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setW(entry.contentRect.width);
    });
    ro.observe(target);
    return () => ro.disconnect();
  }, []);

  if (products.length === 0) {
    return (
      <Box sx={{ color: "var(--ink-3)", padding: "12px", fontSize: "13px" }}>
        Sin productos con datos completos para representar.
      </Box>
    );
  }

  const innerW = Math.max(60, w - PAD_L - PAD_R);
  const innerH = height - PAD_T - PAD_B;
  const maxU = Math.max(...products.map((p) => p.unitsSold), 1) * 1.1;
  const maxM = Math.max(40, ...products.map((p) => p.realMarginPct)) * 1.05;
  const minM = 0;

  const logX = (u: number): number =>
    PAD_L + (Math.log(u + 1) / Math.log(maxU + 1)) * innerW;
  const yFn = (m: number): number =>
    PAD_T + innerH - ((m - minM) / (maxM - minM)) * innerH;

  const maxR = Math.max(...products.map((p) => p.totalMargin), 1);
  const radius = (tm: number): number => 4 + Math.sqrt(tm / maxR) * 18;

  // medianas que dividen los 4 cuadrantes
  const medianM = 22;
  const medianU = Math.exp((Math.log(maxU) + Math.log(1)) / 2);

  const yTicks = [0, 10, 20, 30, 40].filter((t) => t <= maxM);
  const xTicks = [10, 50, 100, 300, 1000].filter((t) => t < maxU);

  const hovered = hover != null ? products[hover] : null;

  return (
    <Box ref={ref} sx={{ position: "relative", width: "100%" }}>
      <Box
        component="svg"
        width={w}
        height={height}
        sx={{ display: "block" }}
        aria-label="Distribución de productos por margen y volumen"
      >
        {/* Tints de cuadrantes */}
        <rect
          x={logX(medianU)}
          y={PAD_T}
          width={PAD_L + innerW - logX(medianU)}
          height={yFn(medianM) - PAD_T}
          fill="var(--pos-soft)"
          opacity="0.5"
        />
        <rect
          x={logX(medianU)}
          y={yFn(medianM)}
          width={PAD_L + innerW - logX(medianU)}
          height={PAD_T + innerH - yFn(medianM)}
          fill="var(--neg-soft)"
          opacity="0.4"
        />

        {/* Líneas threshold */}
        <line
          x1={PAD_L}
          y1={yFn(medianM)}
          x2={PAD_L + innerW}
          y2={yFn(medianM)}
          stroke="var(--ink-4)"
          strokeDasharray="3 3"
        />
        <line
          x1={logX(medianU)}
          y1={PAD_T}
          x2={logX(medianU)}
          y2={PAD_T + innerH}
          stroke="var(--ink-4)"
          strokeDasharray="3 3"
        />

        {/* Ejes */}
        <line
          x1={PAD_L}
          y1={PAD_T + innerH}
          x2={PAD_L + innerW}
          y2={PAD_T + innerH}
          stroke="var(--border-strong)"
        />
        <line
          x1={PAD_L}
          y1={PAD_T}
          x2={PAD_L}
          y2={PAD_T + innerH}
          stroke="var(--border-strong)"
        />

        {/* Y ticks */}
        {yTicks.map((t) => (
          <g key={`y-${t}`}>
            <text
              x={PAD_L - 8}
              y={yFn(t) + 3}
              fontSize="10.5"
              fill="var(--ink-3)"
              textAnchor="end"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {t}%
            </text>
            {t > 0 && (
              <line
                x1={PAD_L}
                x2={PAD_L + innerW}
                y1={yFn(t)}
                y2={yFn(t)}
                stroke="var(--border)"
                strokeDasharray="2 4"
              />
            )}
          </g>
        ))}

        {/* X ticks (log) */}
        {xTicks.map((t) => (
          <text
            key={`x-${t}`}
            x={logX(t)}
            y={PAD_T + innerH + 14}
            fontSize="10.5"
            fill="var(--ink-3)"
            textAnchor="middle"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {t}
          </text>
        ))}

        {/* Labels de ejes */}
        <text
          x={PAD_L + innerW / 2}
          y={height - 4}
          fontSize="10.5"
          fill="var(--ink-3)"
          textAnchor="middle"
        >
          Unidades vendidas (escala log)
        </text>
        <text
          x={12}
          y={PAD_T + innerH / 2}
          fontSize="10.5"
          fill="var(--ink-3)"
          textAnchor="middle"
          transform={`rotate(-90 12 ${PAD_T + innerH / 2})`}
        >
          Margen real %
        </text>

        {/* Etiquetas de cuadrantes */}
        <text
          x={PAD_L + innerW - 8}
          y={PAD_T + 14}
          fontSize="10"
          fill="var(--pos)"
          textAnchor="end"
          fontWeight="600"
        >
          ★ Estrellas
        </text>
        <text
          x={PAD_L + innerW - 8}
          y={PAD_T + innerH - 8}
          fontSize="10"
          fill="var(--neg)"
          textAnchor="end"
          fontWeight="600"
        >
          Volumen sin margen
        </text>
        <text
          x={PAD_L + 8}
          y={PAD_T + 14}
          fontSize="10"
          fill="var(--ink-3)"
          textAnchor="start"
        >
          Nicho rentable
        </text>
        <text
          x={PAD_L + 8}
          y={PAD_T + innerH - 8}
          fontSize="10"
          fill="var(--ink-3)"
          textAnchor="start"
        >
          Bajo aporte
        </text>

        {/* Puntos */}
        {products.map((p, i) => {
          const fill = bubbleColor(p.realMarginPct);
          return (
            <circle
              key={p.id}
              cx={logX(p.unitsSold)}
              cy={yFn(p.realMarginPct)}
              r={radius(p.totalMargin)}
              fill={fill}
              fillOpacity={0.55}
              stroke={fill}
              strokeWidth={1.5}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: "pointer" }}
            />
          );
        })}
      </Box>

      {hovered && (
        <Box
          sx={{
            position: "absolute",
            left: Math.min(logX(hovered.unitsSold) + 8, w - 220),
            top: yFn(hovered.realMarginPct) - 8,
            backgroundColor: "#0d1220",
            color: "#fff",
            borderRadius: "6px",
            padding: "8px 10px",
            fontSize: "12px",
            boxShadow: "0 8px 24px rgba(13, 18, 32, 0.25)",
            pointerEvents: "none",
            minWidth: 160,
          }}
        >
          <Box sx={{ color: "#9aa0ad", fontSize: "11px", marginBottom: "4px" }}>
            {hovered.name}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
            <Box component="span">Unidades</Box>
            <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
              {hovered.unitsSold.toLocaleString("es-MX")}
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
            <Box component="span">Margen real</Box>
            <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
              {hovered.realMarginPct.toFixed(1)}%
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
            <Box component="span">Margen total</Box>
            <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
              {formatValue(hovered.totalMargin)}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
